/**
 * Entity-specific configuration for Connection Creator
 *
 * Different entity types have different rules for:
 * - Which flow types are allowed
 * - How many start nodes (flows) are allowed
 * - Whether flow type can be changed in form
 */

import { StartFlowType } from "components/CC/types/enums";

/**
 * Entity types that can use Connection Creator
 */
export enum CCEntityType {
    StructureValidate = "structureValidate",
    StructureModify = "structureModify",
    StructureAutocomplete = "structureAutocomplete",
    Integration = "integration",
    TrackingIn = "trackingIn",
    TrackingOut = "trackingOut",
}

/**
 * Configuration for a specific entity type
 */
export interface CCEntityConfig {
    /** i18n key for the entity type label (resolved via usePaldockTranslation in UI) */
    labelKey: string;
    /** Allowed flow types for start nodes */
    allowedFlowTypes: StartFlowType[];
    /** Maximum number of start nodes (flows) allowed */
    maxStarts: number;
    /** Default flow type when creating first start */
    defaultFlowType: StartFlowType;
    /**
     * Whether flow type can be changed in form.
     * - "always": can always change (within allowed types)
     * - "never": cannot change
     * - "single": can change only if there's a single start
     */
    canChangeFlowType: "always" | "never" | "single";
    /**
     * For entities with multiple flows, enforce unique flow types
     * (e.g., Integration can have one Ping and one Post, not two Pings)
     */
    enforceUniqueFlowTypes: boolean;
    /**
     * The BE step type key used for this entity's start node.
     * Maps to the key in connection-creator.steps config (e.g., "integration", "incoming_postback").
     * Required when saving new start nodes to the backend.
     */
    startBeType: string;
    /** Whether an existing but empty graph should initialize with the default start layout. */
    showDefaultWhenGraphEmpty: boolean;
}

/**
 * Shared config for all structure entity types — single instant flow
 */
const STRUCTURE_CONFIG_BASE: Omit<CCEntityConfig, "labelKey"> = {
    allowedFlowTypes: ["instant"],
    maxStarts: 1,
    defaultFlowType: "instant",
    canChangeFlowType: "never",
    enforceUniqueFlowTypes: false,
    startBeType: "structure",
    showDefaultWhenGraphEmpty: true,
};

/**
 * Entity configurations
 */
export const CC_ENTITY_CONFIGS: Record<CCEntityType, CCEntityConfig> = {
    [CCEntityType.StructureValidate]: {
        ...STRUCTURE_CONFIG_BASE,
        labelKey: "entity_types.structure_validate",
    },
    [CCEntityType.StructureModify]: {
        ...STRUCTURE_CONFIG_BASE,
        labelKey: "entity_types.structure_modify",
    },
    [CCEntityType.StructureAutocomplete]: {
        ...STRUCTURE_CONFIG_BASE,
        labelKey: "entity_types.structure_autocomplete",
    },

    /**
     * Integration - Can have Ping, Post, and/or Post Verify flows
     * - Max 3 starts (one Ping, one Post, one Post Verify)
     * - Can change flow type if only one start exists
     * - Each start must have a unique flow type
     */
    [CCEntityType.Integration]: {
        labelKey: "entity_types.integration",
        allowedFlowTypes: ["ping", "post", "postVerify"],
        maxStarts: 3,
        defaultFlowType: "post",
        canChangeFlowType: "single",
        enforceUniqueFlowTypes: true,
        startBeType: "integration",
        showDefaultWhenGraphEmpty: false,
    },

    /**
     * Tracking In (Incoming Postback) - Always Webhook, single flow
     */
    [CCEntityType.TrackingIn]: {
        labelKey: "entity_types.tracking_in",
        allowedFlowTypes: ["webhook"],
        maxStarts: 1,
        defaultFlowType: "webhook",
        canChangeFlowType: "never",
        enforceUniqueFlowTypes: false,
        startBeType: "incoming_postback",
        showDefaultWhenGraphEmpty: false,
    },

    /**
     * Tracking Out (Outgoing Postback) - Always Instant, single flow
     */
    [CCEntityType.TrackingOut]: {
        labelKey: "entity_types.tracking_out",
        allowedFlowTypes: ["instant"],
        maxStarts: 1,
        defaultFlowType: "instant",
        canChangeFlowType: "never",
        enforceUniqueFlowTypes: false,
        startBeType: "outgoing_postback",
        showDefaultWhenGraphEmpty: false,
    },
};

/**
 * Get available flow types for adding a new start node
 * Takes into account existing flows and entity rules
 */
export const getAvailableFlowTypesForNewStart = (
    entityType: CCEntityType,
    existingFlowTypes: StartFlowType[]
): StartFlowType[] => {
    const config = CC_ENTITY_CONFIGS[entityType];

    // Check if we can add more starts
    if (existingFlowTypes.length >= config.maxStarts) {
        return [];
    }

    // If enforcing unique flow types, filter out existing ones
    if (config.enforceUniqueFlowTypes) {
        return config.allowedFlowTypes.filter((ft) => !existingFlowTypes.includes(ft));
    }

    return config.allowedFlowTypes;
};

/**
 * Get available flow types for the start form dropdown
 * Takes into account existing flows and whether change is allowed
 */
export const getAvailableFlowTypesForForm = (
    entityType: CCEntityType,
    currentFlowType: StartFlowType | undefined,
    existingFlowTypes: StartFlowType[],
    totalStarts: number
): { options: StartFlowType[]; disabled: boolean } => {
    const config = CC_ENTITY_CONFIGS[entityType];

    // Check if changing is allowed
    if (config.canChangeFlowType === "never") {
        return {
            options: currentFlowType ? [currentFlowType] : config.allowedFlowTypes,
            disabled: true,
        };
    }

    if (config.canChangeFlowType === "single" && totalStarts > 1) {
        return {
            options: currentFlowType ? [currentFlowType] : config.allowedFlowTypes,
            disabled: true,
        };
    }

    // Can change - return allowed options
    // If enforcing unique, exclude other existing flow types (but include current)
    if (config.enforceUniqueFlowTypes) {
        const otherFlowTypes = existingFlowTypes.filter((ft) => ft !== currentFlowType);
        const availableOptions = config.allowedFlowTypes.filter((ft) => !otherFlowTypes.includes(ft));
        return {
            options: availableOptions,
            disabled: false,
        };
    }

    return {
        options: config.allowedFlowTypes,
        disabled: false,
    };
};

/**
 * Check if a new start can be added
 */
export const canAddNewStart = (entityType: CCEntityType, existingFlowTypes: StartFlowType[]): boolean => {
    return getAvailableFlowTypesForNewStart(entityType, existingFlowTypes).length > 0;
};

/**
 * Validate and fix a flow type for a given entity.
 * If the flow type is not allowed, returns the entity's default flow type.
 */
export const validateFlowType = (
    entityType: CCEntityType,
    flowType: StartFlowType | string | undefined
): StartFlowType => {
    const config = CC_ENTITY_CONFIGS[entityType];

    // If flow type is valid for this entity, keep it
    if (flowType && config.allowedFlowTypes.includes(flowType as StartFlowType)) {
        return flowType as StartFlowType;
    }

    // Otherwise return the entity's default
    return config.defaultFlowType;
};
